import { action, makeAutoObservable, observable } from "mobx"

import bundleService, {
  type BundleDraftDto,
  type BundleListDto,
  type CreateBundleDto,
  type GetRoleBundleViewOutput,
  type PagedBundleRequestDto,
  type PagedResultDto,
  type PermissionDto,
  type SaveRolePermissionInput,
  type UpdateBundleDto,
  type AddOrphanInput,
} from "@services/administrator/bundle/bundleService"

class BundleStore {
  @observable isLoading!: boolean
  @observable bundles!: PagedResultDto<BundleListDto>
  @observable roleBundleView: GetRoleBundleViewOutput | null = null
  @observable orphans: PermissionDto[] = []
  @observable drafts: BundleDraftDto[] = []

  constructor() {
    makeAutoObservable(this)
    this.bundles = { items: [], totalCount: 0 }
  }

  @action
  async loadBundles(input: PagedBundleRequestDto) {
    this.isLoading = true
    const result = await bundleService
      .getAll(input)
      .finally(() => (this.isLoading = false))
    this.bundles = result
  }

  @action
  async createBundle(input: CreateBundleDto) {
    const result = await bundleService.create(input)
    return result
  }

  @action
  async updateBundle(input: UpdateBundleDto) {
    const result = await bundleService.update(input)
    return result
  }

  @action
  async deleteBundle(id: string) {
    await bundleService.delete({ id })
  }

  @action
  async cloneBundle(id: string) {
    return await bundleService.clone({ id })
  }

  @action
  async getBundleForEdit(id: string) {
    return await bundleService.getForEdit(id)
  }

  @action
  async getRoleBundleView(roleId: number) {
    this.isLoading = true
    const result = await bundleService
      .getRoleBundleView(roleId)
      .finally(() => (this.isLoading = false))
    this.roleBundleView = result
  }

  @action
  async saveRolePermissions(input: SaveRolePermissionInput) {
    await bundleService.saveRolePermissions(input)
  }

  @action
  async loadOrphans() {
    this.isLoading = true
    const result = await bundleService
      .getOrphans()
      .finally(() => (this.isLoading = false))
    this.orphans = result
  }

  @action
  async addOrphansToBundle(input: AddOrphanInput) {
    await bundleService.addPermissionsToBundle(input)
  }

  @action
  async scanDrafts() {
    this.isLoading = true
    const result = await bundleService
      .scanForBundleDrafts()
      .finally(() => (this.isLoading = false))
    this.drafts = result
  }

  @action
  async approveDraft(name: string) {
    await bundleService.approveDraft({ name })
  }
}

export default BundleStore
